/**
 * HAWKER HUNTER T.7/T.7A LANDING PAGE
 * Interactions + Motion One animations (progressive enhancement)
 *
 * Loaded as ES module only on templates with suffix "hunter".
 * All interactive modules work without Motion One; Motion One only
 * adds scroll reveals / parallax / counters on top.
 */

const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* ------------------------------------------------------------------
   Interactive modules (no dependency on Motion One)
   ------------------------------------------------------------------ */

function initCockpit() {
  const AUTO_MS = 4200;
  const ZOOM = REDUCED ? 1 : 1.6;
  const MAX_SHIFT = 50 * (ZOOM - 1);
  document.querySelectorAll('[data-hunter-cockpit]').forEach((root) => {
    const img = root.querySelector('[data-cockpit-img]');
    const items = [...root.querySelectorAll('[data-cockpit-item]')];
    if (!img || !items.length) return;
    const reticle = root.querySelector('[data-cockpit-reticle]');
    const idxEl = root.querySelector('[data-cockpit-idx]');
    const totalEl = root.querySelector('[data-cockpit-total]');
    const coordEl = root.querySelector('[data-cockpit-coord]');

    let current = 0;
    let timer = null;
    let auto = !REDUCED;

    if (totalEl) totalEl.textContent = String(items.length).padStart(2, '0');

    function clearBar(item) {
      const bar = item.querySelector('.bar i');
      if (bar) { bar.style.transition = 'none'; bar.style.width = '0%'; }
    }
    function runBar(item) {
      const bar = item.querySelector('.bar i');
      if (!bar) return;
      bar.style.transition = 'none';
      bar.style.width = '0%';
      void bar.offsetWidth;
      if (auto) {
        bar.style.transition = `width ${AUTO_MS}ms linear`;
        bar.style.width = '100%';
      }
    }
    function show(i) {
      current = (i + items.length) % items.length;
      const it = items[current];
      const x = parseFloat(it.dataset.x);
      const y = parseFloat(it.dataset.y);
      const px = isNaN(x) ? 50 : x;
      const py = isNaN(y) ? 50 : y;

      let tx = -ZOOM * (px - 50);
      let ty = -ZOOM * (py - 50);
      tx = Math.max(-MAX_SHIFT, Math.min(MAX_SHIFT, tx));
      ty = Math.max(-MAX_SHIFT, Math.min(MAX_SHIFT, ty));
      img.style.transform = `translate(${tx}%, ${ty}%) scale(${ZOOM})`;

      if (reticle) {
        const fx = 50 + ZOOM * (px - 50) + tx;
        const fy = 50 + ZOOM * (py - 50) + ty;
        reticle.style.left = `${fx}%`;
        reticle.style.top = `${fy}%`;
      }
      if (idxEl) idxEl.textContent = String(current + 1).padStart(2, '0');
      if (coordEl) coordEl.textContent = `${px.toFixed(1)} · ${py.toFixed(1)}`;

      items.forEach((el, idx) => {
        const on = idx === current;
        el.classList.toggle('is-active', on);
        if (!on) clearBar(el);
      });
      runBar(it);
    }
    function next() { show(current + 1); }
    function startTimer() { if (auto) { clearInterval(timer); timer = setInterval(next, AUTO_MS); } }
    function stopTimer() { clearInterval(timer); timer = null; }
    function stopAuto() {
      auto = false;
      stopTimer();
      items.forEach(clearBar);
    }

    items.forEach((it, i) => {
      it.addEventListener('click', () => { stopAuto(); show(i); });
      it.addEventListener('mouseenter', () => { stopAuto(); show(i); });
    });

    show(0);

    if (auto && 'IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        entries.forEach((en) => {
          if (!auto) return;
          if (en.isIntersecting) { show(current); startTimer(); }
          else { stopTimer(); }
        });
      }, { threshold: 0.35 });
      io.observe(root);
    } else if (auto) {
      startTimer();
    }
  });
}

function initLivery() {
  document.querySelectorAll('[data-hunter-livery]').forEach((root) => {
    const tabs = root.querySelectorAll('.hunter-livery-tab');
    const imgs = root.querySelectorAll('.hunter-livery__img');
    const metaUnit = root.querySelector('[data-livery-unit]');
    const metaYear = root.querySelector('[data-livery-year]');
    const metaBase = root.querySelector('[data-livery-base]');
    if (!tabs.length) return;

    function select(i) {
      tabs.forEach((t, idx) => t.classList.toggle('is-active', idx === i));
      imgs.forEach((img, idx) => img.classList.toggle('is-active', idx === i));
      const t = tabs[i];
      if (metaUnit) metaUnit.textContent = t.dataset.unit || '';
      if (metaYear) metaYear.textContent = t.dataset.year || '';
      if (metaBase) metaBase.textContent = t.dataset.base || '';
    }
    tabs.forEach((t, i) => t.addEventListener('click', () => select(i)));
    select(0);
  });
}

function initCompare() {
  document.querySelectorAll('[data-hunter-compare]').forEach((el) => {
    const after = el.querySelector('.hunter-compare__after');
    const handle = el.querySelector('.hunter-compare__handle');
    if (!after || !handle) return;
    const grid = el.querySelector('.hunter-compare__grid');
    const pctEl = el.querySelector('[data-compare-pct]');
    let dragging = false;
    let touched = false;
    let current = 50;

    function apply(pct) {
      current = Math.max(2, Math.min(98, pct));
      after.style.clipPath = `inset(0 0 0 ${current}%)`;
      if (grid) grid.style.clipPath = `inset(0 0 0 ${current}%)`;
      handle.style.left = `${current}%`;
      if (pctEl) pctEl.textContent = String(Math.round(100 - current));
    }
    function setPos(clientX) {
      const rect = el.getBoundingClientRect();
      apply(((clientX - rect.left) / rect.width) * 100);
    }
    function markTouched() {
      if (touched) return;
      touched = true;
      el.classList.add('is-touched');
    }

    const start = () => { dragging = true; markTouched(); };
    const stop = () => (dragging = false);
    const move = (e) => {
      if (!dragging) return;
      const x = e.touches ? e.touches[0].clientX : e.clientX;
      setPos(x);
    };
    handle.addEventListener('mousedown', start);
    handle.addEventListener('touchstart', start, { passive: true });
    window.addEventListener('mouseup', stop);
    window.addEventListener('touchend', stop);
    window.addEventListener('mousemove', move);
    window.addEventListener('touchmove', move, { passive: true });
    el.addEventListener('click', (e) => {
      if (e.target === handle) return;
      markTouched();
      const x = e.touches ? e.touches[0].clientX : e.clientX;
      setPos(x);
    });

    apply(50);

    // Inviting auto "scan" sweep the first time it enters the viewport
    if (!REDUCED && 'IntersectionObserver' in window) {
      const keys = [50, 90, 12, 50];
      let played = false;
      function demo() {
        let seg = 0;
        let segStart = performance.now();
        const dur = 780;
        function step(now) {
          if (touched) return;
          const t = Math.min(1, (now - segStart) / dur);
          const e = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
          const from = keys[seg], to = keys[seg + 1];
          apply(from + (to - from) * e);
          if (t >= 1) { seg++; segStart = now; if (seg >= keys.length - 1) return; }
          requestAnimationFrame(step);
        }
        requestAnimationFrame(step);
      }
      const io = new IntersectionObserver((entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting && !played && !touched) {
            played = true;
            setTimeout(demo, 400);
          }
        });
      }, { threshold: 0.45 });
      io.observe(el);
    }
  });
}

function initFaq() {
  document.querySelectorAll('[data-hunter-faq]').forEach((list) => {
    list.querySelectorAll('.hunter-faq__item').forEach((item) => {
      const q = item.querySelector('.hunter-faq__q');
      const a = item.querySelector('.hunter-faq__a');
      if (!q || !a) return;
      q.addEventListener('click', () => {
        const open = item.classList.contains('is-open');
        list.querySelectorAll('.hunter-faq__item').forEach((it) => {
          it.classList.remove('is-open');
          const ans = it.querySelector('.hunter-faq__a');
          if (ans) ans.style.maxHeight = null;
        });
        if (!open) {
          item.classList.add('is-open');
          a.style.maxHeight = a.scrollHeight + 'px';
        }
      });
    });
  });
}

function initAddToCart() {
  document.querySelectorAll('[data-hunter-product-form]').forEach((form) => {
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const btn = form.querySelector('button[type="submit"]');
      if (!btn || btn.disabled) return;
      const original = btn.innerHTML;
      btn.disabled = true;
      btn.innerHTML = '<span>…</span>';
      try {
        const res = await fetch('/cart/add.js', { method: 'POST', body: new FormData(form) });
        if (!res.ok) throw new Error('add failed');
        const cartRes = await fetch('/cart.js');
        const cart = await cartRes.json();
        const cartType = window.themeVariables?.settings?.cartType;
        const openDrawer = cartType === 'drawer';
        document.documentElement.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart } }));
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { cart, openMiniCart: openDrawer } }));
        btn.innerHTML = '<span>✓ Hinzugefügt</span>';
        if (openDrawer) {
          const toggle = document.querySelector('[data-action="toggle-drawer"][data-drawer="cart"]');
          if (toggle && !toggle.classList.contains('is-active')) setTimeout(() => toggle.click(), 120);
        }
        setTimeout(() => { btn.disabled = false; btn.innerHTML = original; }, 2200);
      } catch (err) {
        console.error('[hunter] add to cart', err);
        btn.disabled = false;
        btn.innerHTML = original;
      }
    });
  });
}

// Split target text into per-character spans for kinetic reveals.
function splitTitle() {
  document.querySelectorAll('[data-splittext]').forEach((el) => {
    if (el.dataset.split === 'done') return;
    const words = el.textContent.split(/(\s+)/);
    el.textContent = '';
    words.forEach((w) => {
      if (w === '' ) return;
      if (/^\s+$/.test(w)) { el.appendChild(document.createTextNode(' ')); return; }
      const word = document.createElement('span');
      word.className = 'word';
      [...w].forEach((ch) => {
        const s = document.createElement('span');
        s.className = 'char';
        s.textContent = ch;
        word.appendChild(s);
      });
      el.appendChild(word);
    });
    el.dataset.split = 'done';
  });
}

// Magnetic buttons: subtle pull toward the cursor.
function initMagnetic() {
  if (REDUCED) return;
  document.querySelectorAll('[data-magnetic]').forEach((btn) => {
    const strength = 16;
    btn.addEventListener('pointermove', (e) => {
      const r = btn.getBoundingClientRect();
      const x = (e.clientX - (r.left + r.width / 2)) / r.width;
      const y = (e.clientY - (r.top + r.height / 2)) / r.height;
      btn.style.transform = `translate(${x * strength}px, ${y * strength}px)`;
    });
    btn.addEventListener('pointerleave', () => { btn.style.transform = ''; });
  });
}

// Fixed cinematic overlays: film grain, scroll progress bar, telemetry HUD.
function injectOverlays() {
  if (!document.querySelector('.hunter-lp')) return null;
  const frag = document.createDocumentFragment();

  let grain = null;
  if (!REDUCED) {
    grain = document.createElement('div');
    grain.className = 'hunter-grain';
    frag.appendChild(grain);
  }

  const progress = document.createElement('div');
  progress.className = 'hunter-progress';
  frag.appendChild(progress);

  const tele = document.createElement('div');
  tele.className = 'hunter-telemetry';
  tele.setAttribute('aria-hidden', 'true');
  tele.innerHTML =
    '<div class="row"><span class="lbl">ALT</span><span class="val"><b data-tele-alt>0</b> m</span></div>' +
    '<div class="row"><span class="lbl">SPD</span><span class="val"><b data-tele-spd>0</b> km/h</span></div>' +
    '<div class="row"><span class="lbl">SYS</span><span class="val">HUNTER&nbsp;T.7</span></div>';
  frag.appendChild(tele);

  let jet = null;
  if (!REDUCED) {
    jet = document.createElement('div');
    jet.className = 'hunter-flyjet';
    jet.setAttribute('aria-hidden', 'true');
    jet.innerHTML =
      '<svg viewBox="0 0 64 64"><path d="M32 2 L36 26 L60 40 L60 46 L36 38 L36 54 L44 60 L44 63 L32 58 L20 63 L20 60 L28 54 L28 38 L4 46 L4 40 L28 26 Z"/></svg>';
    frag.appendChild(jet);
  }

  document.body.appendChild(frag);
  return { progress, tele, jet };
}

// Drive the progress bar + telemetry readout from scroll position.
function initScrollHud(overlays) {
  if (!overlays) return;
  const { progress, tele, jet } = overlays;
  const altEl = tele.querySelector('[data-tele-alt]');
  const spdEl = tele.querySelector('[data-tele-spd]');
  const ALT_MAX = 12000;
  const SPD_MAX = 1150;
  let visible = false;
  let ticking = false;

  function update() {
    ticking = false;
    const h = document.documentElement;
    const max = h.scrollHeight - h.clientHeight;
    const p = max > 0 ? Math.min(1, Math.max(0, h.scrollTop / max)) : 0;
    if (progress) progress.style.width = (p * 100).toFixed(2) + '%';
    if (altEl) altEl.textContent = Math.round(p * ALT_MAX).toLocaleString('de-DE');
    if (spdEl) spdEl.textContent = Math.round(p * SPD_MAX).toLocaleString('de-DE');
    if (jet) {
      const x = -14 + p * 128;
      const wobble = Math.sin(p * Math.PI * 3) * 4;
      const y = 84 - p * 70 + wobble;
      const bank = 48 + Math.sin(p * Math.PI * 3) * 7;
      jet.style.transform = `translate(${x}vw, ${y}vh) rotate(${bank}deg)`;
    }
    const shouldShow = h.scrollTop > window.innerHeight * 0.5;
    if (shouldShow !== visible) {
      visible = shouldShow;
      tele.classList.toggle('is-visible', visible);
    }
  }
  window.addEventListener('scroll', () => {
    if (!ticking) { ticking = true; requestAnimationFrame(update); }
  }, { passive: true });
  update();
}

/* ------------------------------------------------------------------
   Motion One enhancements (optional)
   ------------------------------------------------------------------ */

// Cinematic hero: kinetic title, HUD boot sequence, reticle lock,
// scroll + pointer parallax on the layered background.
function initHeroCinematic(motion) {
  const { animate, scroll, stagger } = motion;
  const hero = document.querySelector('[data-hunter-hero]');
  if (!hero) return;

  const chars = hero.querySelectorAll('.hunter-hero__title-main .char');
  if (chars.length) {
    chars.forEach((c) => { c.style.opacity = 0; });
    animate(
      chars,
      { opacity: [0, 1], transform: ['translateY(115%) rotate(5deg)', 'translateY(0) rotate(0deg)'] },
      { duration: 0.9, delay: stagger(0.04, { start: 0.15 }), easing: [0.16, 1, 0.3, 1] }
    );
  }

  hero.querySelectorAll('[data-hero-fade]').forEach((el, i) => {
    el.style.opacity = 0;
    animate(
      el,
      { opacity: [0, 1], transform: ['translateY(22px)', 'translateY(0)'] },
      { duration: 0.7, delay: 0.5 + i * 0.12, easing: [0.16, 1, 0.3, 1] }
    );
  });

  const boots = hero.querySelectorAll('[data-hud-boot]');
  if (boots.length) {
    boots.forEach((b) => { b.style.opacity = 0; });
    animate(boots, { opacity: [0, 0.9] }, { duration: 0.5, delay: stagger(0.08, { start: 0.35 }) });
  }

  const ret = hero.querySelector('[data-hero-reticle]');
  if (ret) {
    ret.style.opacity = 0;
    animate(
      ret,
      { opacity: [0, 1], transform: ['scale(1.5)', 'scale(1)'] },
      { duration: 0.8, delay: 0.65, easing: [0.16, 1, 0.3, 1] }
    );
  }

  const jet = hero.querySelector('[data-hero-jet]');
  if (jet) {
    scroll(
      animate(jet, { transform: ['translateY(0px)', 'translateY(80px)'] }, { easing: 'linear' }),
      { target: hero, offset: ['start start', 'end start'] }
    );
  }

  const layers = hero.querySelectorAll('[data-hero-layer]');
  if (layers.length) {
    hero.addEventListener('pointermove', (e) => {
      const r = hero.getBoundingClientRect();
      const dx = (e.clientX - (r.left + r.width / 2)) / r.width;
      const dy = (e.clientY - (r.top + r.height / 2)) / r.height;
      layers.forEach((l) => {
        const d = parseFloat(l.dataset.heroLayer) || 0.1;
        l.style.transform = `translate(${dx * d * 90}px, ${dy * d * 90}px)`;
      });
    });
  }
}

function initMotion(motion) {
  const { animate, inView, scroll, stagger } = motion;
  document.documentElement.classList.add('hunter-js-ready');

  initHeroCinematic(motion);

  // Kinetic titles outside the hero (e.g. CTA finale) reveal per character
  document.querySelectorAll('[data-splittext]').forEach((el) => {
    if (el.closest('[data-hunter-hero]')) return;
    const chars = el.querySelectorAll('.char');
    if (!chars.length) return;
    chars.forEach((c) => { c.style.opacity = 0; });
    inView(el, () => {
      animate(
        chars,
        { opacity: [0, 1], transform: ['translateY(110%) rotate(4deg)', 'translateY(0) rotate(0deg)'] },
        { duration: 0.7, delay: stagger(0.03), easing: [0.16, 1, 0.3, 1] }
      );
    }, { amount: 0.35 });
  });

  // Cinematic CTA background parallax
  document.querySelectorAll('[data-cta-bg]').forEach((el) => {
    const target = el.closest('.hunter-cta') || el;
    scroll(
      animate(el, { transform: ['translateY(-6%)', 'translateY(6%)'] }, { easing: 'linear' }),
      { target, offset: ['start end', 'end start'] }
    );
  });

  // Reveal on scroll (with soft focus-pull)
  const reveals = document.querySelectorAll('[data-reveal]');
  reveals.forEach((el) => {
    const dir = el.dataset.reveal || 'up';
    const from =
      dir === 'left' ? 'translateX(-40px)' :
      dir === 'right' ? 'translateX(40px)' :
      'translateY(44px)';
    el.style.opacity = 0;
    inView(el, () => {
      animate(
        el,
        { opacity: [0, 1], filter: ['blur(10px)', 'blur(0px)'], transform: [from, 'translate(0px, 0px)'] },
        { duration: 0.8, easing: [0.16, 1, 0.3, 1] }
      );
    }, { amount: 0.2 });
  });

  // Staggered groups
  document.querySelectorAll('[data-reveal-group]').forEach((group) => {
    const children = Array.from(group.children);
    children.forEach((c) => (c.style.opacity = 0));
    inView(group, () => {
      animate(children, { opacity: [0, 1], transform: ['translateY(30px)', 'translateY(0px)'] }, { duration: 0.6, delay: stagger(0.08), easing: [0.16, 1, 0.3, 1] });
    }, { amount: 0.2 });
  });

  // Instrument gauge rings (CSS transition, driven on view)
  document.querySelectorAll('[data-gauge]').forEach((fact) => {
    const ring = fact.querySelector('[data-gauge-ring]');
    if (!ring) return;
    const pct = Math.max(0, Math.min(100, parseFloat(fact.dataset.gauge) || 78));
    const r = ring.r.baseVal.value;
    const circ = 2 * Math.PI * r;
    ring.style.strokeDasharray = String(circ);
    ring.style.strokeDashoffset = String(circ);
    inView(fact, () => {
      requestAnimationFrame(() => { ring.style.strokeDashoffset = String(circ * (1 - pct / 100)); });
    }, { amount: 0.5 });
  });

  // Counters
  document.querySelectorAll('[data-count]').forEach((el) => {
    const target = parseFloat(el.dataset.count);
    const decimals = (el.dataset.count.split('.')[1] || '').length;
    inView(el, () => {
      animate(0, target, {
        duration: 1.4,
        easing: [0.16, 1, 0.3, 1],
        onUpdate: (v) => { el.textContent = decimals ? v.toFixed(decimals) : Math.round(v).toString(); },
      });
    }, { amount: 0.6 });
  });

  // Detail cards subtle parallax
  document.querySelectorAll('[data-parallax]').forEach((el) => {
    const depth = parseFloat(el.dataset.parallax) || 30;
    scroll(animate(el, { transform: [`translateY(-${depth}px)`, `translateY(${depth}px)`] }, { easing: 'linear' }), {
      target: el.closest('.hunter-detail-card') || el,
      offset: ['start end', 'end start'],
    });
  });

  // Exploded view: parts gather over the model, then float apart on scroll
  const isNarrow = window.matchMedia('(max-width: 780px)').matches;
  if (!isNarrow) {
    document.querySelectorAll('[data-hunter-exploded]').forEach((section) => {
      const centerEl = section.querySelector('.hunter-exploded__center');
      const parts = Array.from(section.querySelectorAll('.hunter-part:not(.hunter-exploded__center)'));
      if (!centerEl || !parts.length) return;

      const inners = new Map();
      function measure() {
        const c = centerEl.getBoundingClientRect();
        const cx = c.left + c.width / 2;
        const cy = c.top + c.height / 2;
        parts.forEach((p) => {
          const r = p.getBoundingClientRect();
          const gx = cx - (r.left + r.width / 2);
          const gy = cy - (r.top + r.height / 2);
          inners.set(p, { inner: p.querySelector('.hunter-part__inner'), gx, gy });
        });
      }
      measure();
      window.addEventListener('resize', measure, { passive: true });

      scroll(
        (progress) => {
          const gather = 1 - Math.min(1, progress / 0.85);
          const fade = Math.min(1, Math.max(0, (progress - 0.05) / 0.4));
          parts.forEach((p) => {
            const d = inners.get(p);
            if (!d || !d.inner) return;
            d.inner.style.transform = `translate(${d.gx * gather}px, ${d.gy * gather}px) scale(${0.9 + 0.1 * (1 - gather)})`;
            d.inner.style.opacity = fade;
          });
        },
        { target: section, offset: ['start start', 'end end'] }
      );
    });
  }
}

/* ------------------------------------------------------------------
   Boot
   ------------------------------------------------------------------ */

function boot() {
  initCockpit();
  initLivery();
  initCompare();
  initFaq();
  initAddToCart();
  splitTitle();
  initMagnetic();
  const overlays = injectOverlays();
  initScrollHud(overlays);

  if (REDUCED) return;
  import('https://cdn.jsdelivr.net/npm/motion@11/+esm')
    .then((motion) => initMotion(motion))
    .catch((err) => console.warn('[hunter] Motion One not loaded, content stays visible.', err));
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}
